import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { ArcgisFieldValuesResult } from '../../types/dataSource';
import { TenantVegetationType } from '../../types/tenantVegetationType';
import { useTenantVegetationTypes } from '../../api/tenantVegetationType';

/** One attribute value in the data and the vegetation type it stands for
 *  (config.veg_class_map); a blank vegClass means "use the default". */
export interface VegTypeValueRow {
  value: string;
  vegClass: string;
}

export interface RevegetationTypeState {
  /** `fixed`: every feature gets the default type. `field`: each feature's
   *  type comes from an attribute, via the value rows. */
  mode: 'fixed' | 'field';
  /** The attribute holding each feature's vegetation type (field mode). */
  sourceColumn: string;
  rows: VegTypeValueRow[];
}

export const EMPTY_REVEGETATION_TYPE: RevegetationTypeState = {
  mode: 'fixed',
  sourceColumn: '',
  rows: [],
};

interface RevegetationTypeMappingProps {
  value: RevegetationTypeState;
  onChange: (value: RevegetationTypeState) => void;
  /** The type every feature gets (fixed mode) or falls back to (field mode). */
  defaultVegClass: string;
  onDefaultVegClassChange: (vegClass: string) => void;
  /** Field names known for this source (from Test connection). */
  columnOptions?: string[];
  /** The source is a tested ArcGIS layer, so its values can be listed. */
  canLoadValues?: boolean;
  loadValues?: (field: string) => Promise<ArcgisFieldValuesResult>;
  disabled?: boolean;
}

/** Picks a vegetation type from the organisation's library by name. A saved
 *  name that is no longer in the library stays selectable so it is visible. */
function VegTypePicker({
  types,
  value,
  onChange,
  label,
  helperText,
  disabled,
}: {
  types: TenantVegetationType[];
  value: string;
  onChange: (vegClass: string) => void;
  label: string;
  helperText?: string;
  disabled?: boolean;
}) {
  const names = useMemo(() => {
    const list = types.map((t) => t.veg_class);
    return value && !list.includes(value) ? [value, ...list] : list;
  }, [types, value]);
  const modelByName = useMemo(
    () => new Map(types.map((t) => [t.veg_class, t.model_class_name])),
    [types],
  );
  return (
    <Autocomplete
      size="small"
      fullWidth
      options={names}
      value={value || null}
      disabled={disabled}
      onChange={(_e, next) => onChange(next ?? '')}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        const model = modelByName.get(option);
        return (
          <li key={key} {...rest}>
            <Box>
              <Typography variant="body2">{option}</Typography>
              {model && (
                <Typography variant="caption" color="text.secondary">
                  {model}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
    />
  );
}

/** The vegetation type a revegetation source gives its features: one type
 *  for all of them, or per feature from an attribute whose values are matched
 *  to types from the organisation's vegetation library. */
export default function RevegetationTypeMapping({
  value,
  onChange,
  defaultVegClass,
  onDefaultVegClassChange,
  columnOptions = [],
  canLoadValues = false,
  loadValues,
  disabled = false,
}: RevegetationTypeMappingProps) {
  const { data: typesResp, isLoading: typesLoading } = useTenantVegetationTypes();
  const types = useMemo<TenantVegetationType[]>(() => typesResp?.data ?? [], [typesResp]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{ field: string; count: number; truncated: boolean } | null>(null);
  const lastAutoLoad = useRef<string | null>(null);
  const loadValuesRef = useRef(loadValues);
  loadValuesRef.current = loadValues;

  const canonicalByLower = useMemo(
    () => new Map(types.map((t) => [t.veg_class.toLowerCase(), t.veg_class])),
    [types],
  );

  const setRows = (rows: VegTypeValueRow[]) => onChange({ ...value, rows });

  const handleLoad = async (field: string) => {
    const load = loadValuesRef.current;
    if (!load || !field.trim()) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await load(field.trim());
      const existing = new Map(value.rows.map((r) => [r.value.trim(), r.vegClass]));
      const rows = result.values.map((v) => ({
        value: v,
        // Keep a choice already made for this value; otherwise a value that
        // is itself a library name is matched straight away.
        vegClass: existing.get(v) ?? canonicalByLower.get(v.toLowerCase()) ?? '',
      }));
      onChange({ ...value, sourceColumn: result.field, rows });
      setLoaded({ field: result.field, count: result.values.length, truncated: result.truncated });
    } catch (err) {
      setLoadError((err as { message?: string })?.message ?? 'Could not read the values from the layer');
    } finally {
      setLoading(false);
    }
  };

  // Once the connection is tested, picking a field lists its values without
  // a second click; a field that was already loaded is not fetched again.
  useEffect(() => {
    const field = value.sourceColumn.trim();
    if (value.mode !== 'field' || !canLoadValues || !field || disabled) return;
    if (lastAutoLoad.current === field) return;
    lastAutoLoad.current = field;
    void handleLoad(field);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.mode, value.sourceColumn, canLoadValues, disabled]);

  const handleModeChange = (mode: 'fixed' | 'field') => {
    onChange({ ...value, mode });
  };

  const handleColumnChange = (column: string) => {
    if (column === value.sourceColumn) return;
    setLoaded(null);
    setLoadError(null);
    onChange({ ...value, sourceColumn: column, rows: [] });
  };

  const isField = value.mode === 'field';
  const unmatched = value.rows.filter((r) => r.value.trim() && !r.vegClass).length;
  // A field name saved before the connection was tested (or typed for a
  // shapefile) stays selectable even when the layer does not list it.
  const fieldOptions = useMemo(() => {
    const column = value.sourceColumn.trim();
    return column && !columnOptions.includes(column) ? [column, ...columnOptions] : columnOptions;
  }, [columnOptions, value.sourceColumn]);

  return (
    <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Vegetation Type
        </Typography>
        <RadioGroup
          row
          value={value.mode}
          onChange={(e) => handleModeChange(e.target.value as 'fixed' | 'field')}
        >
          <FormControlLabel
            value="fixed"
            control={<Radio size="small" disabled={disabled} />}
            label="Same type for every feature"
          />
          <FormControlLabel
            value="field"
            control={<Radio size="small" disabled={disabled} />}
            label="From a field in the data"
          />
        </RadioGroup>
      </Box>

      {isField && (
        <>
          {columnOptions.length > 0 ? (
            <Autocomplete
              size="small"
              fullWidth
              options={fieldOptions}
              value={value.sourceColumn || null}
              disabled={disabled}
              onChange={(_e, next) => handleColumnChange(next ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Field holding the vegetation type"
                  helperText="One of the layer's fields, listed by Test connection"
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Field holding the vegetation type"
              value={value.sourceColumn}
              disabled={disabled}
              onChange={(e) => handleColumnChange(e.target.value)}
              helperText={
                canLoadValues
                  ? 'Type the field name exactly as the layer spells it'
                  : 'Type the field name. For an ArcGIS layer, use Test connection above to pick from its fields and list their values.'
              }
            />
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
              Match each value in the data to a type from your vegetation library. A value that is
              already a library name is matched for you; anything left unmatched gets the default below.
            </Typography>
            {canLoadValues && (
              <Tooltip title="Read the values this field holds in the layer">
                <span>
                  <Button
                    size="small"
                    startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                    onClick={() => void handleLoad(value.sourceColumn)}
                    disabled={disabled || loading || !value.sourceColumn.trim()}
                  >
                    {loaded ? 'Reload values' : 'Load values'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {loadError && <Alert severity="error">{loadError}</Alert>}
          {loaded && !loading && (
            <Alert severity={loaded.truncated ? 'warning' : 'info'} sx={{ py: 0 }}>
              {loaded.count === 0
                ? `The layer has no values in ${loaded.field}.`
                : `${loaded.count} value${loaded.count === 1 ? '' : 's'} found in ${loaded.field}` +
                  (unmatched > 0 ? `, ${unmatched} still to match` : '') +
                  '.'}
              {loaded.truncated
                ? ' The list was cut short: this field holds more distinct values than can be mapped one by one.'
                : ''}
            </Alert>
          )}

          {value.rows.map((row, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                label="Value in data"
                value={row.value}
                disabled={disabled}
                onChange={(e) => setRows(value.rows.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)))}
                sx={{ flex: 1 }}
              />
              <Box sx={{ flex: 1.4 }}>
                <VegTypePicker
                  types={types}
                  value={row.vegClass}
                  onChange={(vegClass) => setRows(value.rows.map((r, i) => (i === index ? { ...r, vegClass } : r)))}
                  label="Vegetation type"
                  disabled={disabled || typesLoading}
                />
              </Box>
              <Tooltip title="Remove">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setRows(value.rows.filter((_, i) => i !== index))}
                    disabled={disabled}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}

          {!disabled && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRows([...value.rows, { value: '', vegClass: '' }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add value
            </Button>
          )}
        </>
      )}

      <VegTypePicker
        types={types}
        value={defaultVegClass}
        onChange={onDefaultVegClassChange}
        label={isField ? 'Default type for unmatched values' : 'Vegetation type'}
        helperText={
          typesLoading
            ? 'Loading your vegetation library…'
            : isField
            ? 'Optional. Features whose value is not matched above get this type.'
            : 'From your vegetation library (Vegetation Management).'
        }
        disabled={disabled || typesLoading}
      />
    </Box>
  );
}
